// Single source of truth for expansion kits and which devices each one supports.
// Shared by ExpansionKitSelection (device picker) and MakeAdmin (kit dropdown).
//
// `src`     -> renderImage key for the kit's picture
// `devices` -> devices that support this kit (used both to show device cards
//              and to decide which kits an admin can tag a subprogram with)
export const EXPANSION_KITS = {
    makeyExpansionKit: {
        name: 'Makey Expansion kit',
        src: 'makeyExpansionKit',
        devices: ['Toki'],
    },
    ioExpansionKit1: {
        name: 'IO Expansion kit 1',
        src: 'ioExpansionKit1',
        devices: ['Toki', 'Moki', 'Poki'],
    },
    ioExpansionKit2: {
        name: 'IO Expansion kit 2',
        src: 'ioExpansionKit2',
        devices: ['Moki', 'Poki'],
    },
    roverExpansionKit1: {
        name: 'Rover Expansion kit 1',
        src: 'roverExpansionKit1',
        devices: ['Toki', 'Poki'],
    },
    roverExpansionKit2: {
        name: 'Rover Expansion kit 2',
        src: 'roverExpansionKit2',
        devices: ['Toki', 'Poki'],
    },
    woofExpansionKit3: {
        name: 'Woof Expansion kit 3',
        src: 'woofExpansionKit3',
        devices: ['Poki', 'Moki'],
    },
    roarmExpansionKit4: {
        name: 'RoArm Expansion kit 4',
        src: 'roarmExpansionKit4',
        devices: ['Poki', 'Moki'],
    },
    ioExpansionKit3: {
        name: 'IO Expansion kit 3',
        src: 'ioExpansionKit3',
        devices: ['Poki', 'Moki'],
    },
    homeAutomationKit: {
        name: 'Home Automation kit',
        src: 'homeAutomationKit',
        devices: ['Poki', 'Moki'],
    },
    industrialExpansionKit: {
        name: 'Industrial Expansion kit',
        src: 'industrialExpansionKit',
        devices: ['Poki', 'Moki'],
    },
    walkerExpansionKit5: {
        name: 'Walker Expansion kit 5',
        src: 'walkerExpansionKit5',
        devices: ['Poki'],
    },
    omniBotRoverExpansionKit: {
        name: 'Omni bot Rover Expansion kit',
        src: 'omniBotRoverExpansionKit',
        devices: ['Moki'],
    },
    omniBotShooterExpansionKit: {
        name: 'Omni bot Shooter Expansion kit',
        src: 'omniBotShooterExpansionKit',
        devices: ['Moki'],
    },
    spidyExpansionKit: {
        name: 'Spidy Expansion kit',
        src: 'spidyExpansionKit',
        devices: ['Moki'],
    },
    integratedRoboticsExpansionKit: {
        name: 'Integrated Robotics Expansion kit',
        src: 'integratedRoboticsExpansionKit',
        devices: [],
    },
}

// Returns the kits a given device supports, as [{ id, name, src, devices }].
export const getKitsForDevice = (device) =>
    Object.entries(EXPANSION_KITS)
        .filter(([, kit]) => kit.devices.includes(device))
        .map(([id, kit]) => ({ id, ...kit }))

// Curated per-device row order + label for the "Expansion Kits" tab in Kit
// Selection (device heading + its kits scrolling sideways). Reuses the same
// EXPANSION_KITS ids/images already backing the kit -> device picker flow, so
// a card here still opens the exact same working flow; `label` is what's
// shown on this tab specifically and can be shorter than the kit's full
// `name` above (e.g. Toki's "Rover" vs Poki/Moki's "Rover Kit 1").
// Kits not listed here (Walker, Omni Bot Rover/Shooter, Spidy, Integrated
// Robotics) stay in EXPANSION_KITS for the device-picker flow and any
// existing backend-tagged content, but are intentionally left off this tab.
export const EXPANSION_KIT_TAB_GROUPS = [
    {
        device: 'Toki',
        kits: [
            { id: 'makeyExpansionKit', label: 'Makey' },
            { id: 'roverExpansionKit1', label: 'Rover' },
            { id: 'ioExpansionKit1', label: 'I/O Expansion' },
        ],
    },
    {
        device: 'Poki',
        kits: [
            { id: 'roverExpansionKit1', label: 'Rover Kit 1' },
            { id: 'roverExpansionKit2', label: 'Rover Kit 2' },
            { id: 'ioExpansionKit1', label: 'I/O Expansion Kit 1' },
            { id: 'ioExpansionKit2', label: 'I/O Expansion Kit 2' },
            { id: 'ioExpansionKit3', label: 'I/O Expansion Kit 3' },
            { id: 'woofExpansionKit3', label: 'Woof Expansion Kit' },
            { id: 'roarmExpansionKit4', label: 'Roarm Expansion Kit' },
            { id: 'homeAutomationKit', label: 'Home Automation Kit' },
            { id: 'industrialExpansionKit', label: 'Industrial Expansion Kit' },
        ],
    },
    {
        device: 'Moki',
        kits: [
            { id: 'roverExpansionKit1', label: 'Rover Kit 1' },
            { id: 'roverExpansionKit2', label: 'Rover Kit 2' },
            { id: 'ioExpansionKit1', label: 'I/O Expansion Kit 1' },
            { id: 'ioExpansionKit2', label: 'I/O Expansion Kit 2' },
            { id: 'ioExpansionKit3', label: 'I/O Expansion Kit 3' },
            { id: 'woofExpansionKit3', label: 'Woof Expansion Kit' },
            { id: 'roarmExpansionKit4', label: 'Roarm Expansion Kit' },
            { id: 'homeAutomationKit', label: 'Home Automation Kit' },
            { id: 'industrialExpansionKit', label: 'Industrial Expansion Kit' },
        ],
    },
]
